import PropTypes, { InferProps } from 'prop-types';
import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { type RouteComponentProps } from 'react-router-dom';
import { Col, Container, Row } from 'reactstrap';
import { ReduxState } from 'types';
import { getPerformancePhases } from '../../utils/models/Performance';
import Page from '../Layout/Pages/Page';
import Loading from '../Widgets/Loading';
import PerformanceNav from './PerformanceNav';

const PerformancePage: FC<Props & RouteComponentProps> = ({
  // we want visually to have all parts of the flow
  // wrapped in cards for contrast/visual accessibility,
  // but we allow individual pages to opt out of this
  // if they have cards throughout instead
  wrapBodyInCard = true,
  ...props
}) => {
  const { formatMessage } = useIntl();

  const navPhases = useMemo(() => {
    if (!props.campaign) {
      return null;
    }

    return (
      props.phases ||
      getPerformancePhases(
        props.me,
        props.currentOrganization,
        props.campaign,
        props.currentPerfSurveyResponse,
        props.demoPeople,
        formatMessage
      )
    );
  }, [
    formatMessage,
    props.campaign,
    props.currentOrganization,
    props.demoPeople,
    props.me,
    props.phases,
    props.currentPerfSurveyResponse,
  ]);

  if (!props.campaign) {
    return <Loading />;
  }

  return (
    <Container>
      <Row>
        <Col md={3}>
          <PerformanceNav
            // @ts-expect-error
            campaign={props.campaign}
            history={props.history}
            phases={navPhases}
          />
        </Col>
        <Col>
          <Page
            containerClassName="p-0"
            className={props.className}
            title={props.title}
            plainTextTitle={props.plainTextTitle}
            person={props.person}
            subtitle={props.subtitle}
            wrapBodyInCard={wrapBodyInCard}
          >
            {props.children}
          </Page>
        </Col>
      </Row>
    </Container>
  );
};

const PerformancePage_propTypes = {
  className: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  plainTextTitle: PropTypes.string,
  person: PropTypes.object,
  subtitle: PropTypes.node,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  history: PropTypes.object.isRequired,
  me: PropTypes.object.isRequired,
  currentOrganization: PropTypes.object.isRequired,
  campaign: PropTypes.object.isRequired,
  demoPeople: PropTypes.arrayOf(PropTypes.object).isRequired,
  phases: PropTypes.arrayOf(PropTypes.object),
  wrapBodyInCard: PropTypes.bool,
  currentPerfSurveyResponse: PropTypes.object,
};

const mapStateToProps = (state: ReduxState) => {
  const { me, currentOrganization, demoPeople, currentPerfSurveyResponse } =
    state;

  return {
    me,
    currentOrganization,
    demoPeople,
    currentPerfSurveyResponse,
  };
};

type Props = InferProps<typeof PerformancePage_propTypes>;

export default connect(mapStateToProps)(
  withRouter(React.memo(PerformancePage))
);
